import React, { ReactNode, FC } from 'react';
import cx from 'classnames';
import { LinkProps, useRouteMatch } from 'react-router-dom';

/**
 * @typedef {LeftNavItemProps}
 * @property {ReactNode} children sub-menu items for the nav link
 * @property {boolean} disabled optional; whether to disable the nav link
 * @property {function} handleClick optional click handler for nav link
 * @property {FC<IconProps>} Icon an icon component to show next to the nav link
 * @property {FC<LinkProps>} Link the Link component from 'react-router-dom'. This needs to be passed
 * in from the consuming application so that it has access to the BrowserRouter context; otherwise
 * we'll see an error like: `You should not use <Link> outside a <Router>`.
 * @property {string} linkTo passed to the Link component's `to` prop; represents the link location
 * - see {@link https://reactrouter.com/web/api/Link}
 * @property {string} label the text shown in the nav link
 * @property {string} path the path for the feature, used to determine if the route is active. This
 * may be shorter than the linkTo, such as '/statements'
 */
interface LeftNavItemProps {
    children?: ReactNode;
    disabled?: boolean;
    handleClick?(): void;
    Icon: FC<IconProps>;
    Link: FC<LinkProps>;
    linkTo: LinkProps['to'];
    label: string;
    path: string;
}

const iconFillColors = {
    default: 'tertiary-400',
    active: 'primary-500',
};

/**
 * A navigation menu item. Children are only displayed when the LeftNavItem is active, i.e. has the
 * currently active route.
 * @param {LeftNavItemProps} props
 */
export const LeftNavItem = ({
    children,
    disabled = false,
    handleClick,
    Link,
    linkTo,
    Icon,
    label,
    path,
}: LeftNavItemProps): JSX.Element => {
    const match = useRouteMatch(path);
    const isActive = match !== null;

    const iconFillColor = isActive
        ? iconFillColors.active
        : iconFillColors.default;

    return (
        <div className="flex flex-col">
            <div className="border-r border-tertiary-200 w-full h-6" />
            <div className="flex-grow flex justify-between w-full h-8 pl-6">
                <button
                    type="button"
                    className={cx(
                        'nav-button text-sm font-medium tracking-wider flex justify-start',
                        {
                            'text-primary-500': isActive,
                            'text-tertiary-300': disabled && !isActive,
                            'cursor-default': disabled,
                        },
                    )}
                    disabled={disabled}
                    onClick={(): void => {
                        if (!disabled && handleClick) {
                            handleClick();
                        }
                    }}
                >
                    <Icon fillColor={iconFillColor} width={20} />
                    <div className="pl-2">
                        {!disabled && <Link to={linkTo}>{label}</Link>}
                        {disabled && <>{label}</>}
                    </div>
                </button>
                <div
                    className={cx('border-solid', {
                        'border-r-4 border-primary-500 rounded-full': isActive,
                        'border-r border-tertiary-200': !isActive,
                    })}
                />
            </div>
            {isActive && children && (
                <div
                    className="border-r border-tertiary-200 w-full"
                    style={{
                        paddingLeft: 52,
                    }}
                >
                    <div className="nav-child-button">{children}</div>
                </div>
            )}
        </div>
    );
};
